/**
 * LegalTemplates PDF Editor — shared context: element handles, config, state, constants
 *
 * Split from the former public/js/pdf-editor.js IIFE (Phase 1, Sep 2026).
 * Page config is injected by the Blade view as window.pdfEditorConfig; pdf.js
 * and pdf-lib are bundled from npm through context.js (no CDN).
 */
// Rendering (pdf.js, Apache-2.0) and writing (pdf-lib, MIT) libraries are
// bundled from npm — no CDN. The legacy pdf.js build keeps older phones
// working; its worker is a module worker served from our own build folder.
// pdf-lib (~200 KB gzipped) and fontkit (~340 KB) are only needed when a
// document is written, so they load on first use, not with the page.
import * as pdfjsLib from 'pdfjs-dist/legacy/build/pdf.mjs';
// `?worker&url` makes Vite bundle the worker itself and emit it as a .js
// file. The `?url` copy kept pdf.js's .mjs name, and a web server without an
// .mjs MIME mapping (production nginx, Sep 8 2026) served it as a binary
// blob: the module worker never started and every document hung at
// "Loading PDF bytes…". A .js file is JavaScript to every server.
import pdfWorkerUrl from 'pdfjs-dist/legacy/build/pdf.worker.min.mjs?worker&url';
import { draftDbRequest } from './drafts.js';

export { pdfjsLib, pdfWorkerUrl };

// Lazily loaded writing libraries. Call ensurePdfLib()/ensureFontkit() at the
// top of any async function that reaches for libs.PDFLib / libs.fontkit.
export const libs = {
    PDFLib: null,
    fontkit: null,
};

export async function ensurePdfLib() {
    if (!libs.PDFLib) {
        libs.PDFLib = await import('pdf-lib');
    }
    return libs.PDFLib;
}

export async function ensureFontkit() {
    if (!libs.fontkit) {
        const module = await import('@pdf-lib/fontkit');
        libs.fontkit = module.default || module;
    }
    return libs.fontkit;
}

export const cfg = window.pdfEditorConfig || {};

// Mutable page-level values. They were top-level `let`s in the IIFE; ES
// module bindings cannot be reassigned across modules, so they live here.
export const session = {
    sourceUrl: cfg.sourceUrl ?? null,
    docName: cfg.docName ?? 'document',
    // Guest editor: the id of THIS tab's document. Every request that reads
    // or writes the working file carries it (see publicDocUrl in core.js), so
    // two tabs on the same session never overwrite each other.
    docId: cfg.publicEditorDocId ?? null,
    // A dashboard document with e-sign activity: every save becomes a copy.
    // Cleared once the tab has been moved onto the (unlocked) copy.
    lockedForDirectEdit: Boolean(cfg.documentLockedForDirectEdit),
    signatureEmbedInitialized: false,
    // Poll handle for a queued OCR pass after a redacted save (ocr.js).
    ocrPoll: null,
};

export const saveUrl = cfg.saveUrl ?? null;

export const convertUrl = cfg.convertUrl ?? null;

export const compressUrl = cfg.compressUrl ?? null;

export const mergeUrl = cfg.mergeUrl ?? null;

export const splitExtractUrl = cfg.splitExtractUrl ?? null;

export const printUrl = cfg.printUrl ?? null;

export const downloadUrl = cfg.downloadUrl ?? null;

export const sendUrl = cfg.sendUrl ?? null;

export const signatureRequestUrl = cfg.signatureRequestUrl ?? null;

export const csrfToken = cfg.csrfToken ?? null;

export const sendSenderEmail = cfg.sendSenderEmail ?? null;

export const sendSenderFirstName = cfg.sendSenderFirstName ?? null;

export const sendSenderLastName = cfg.sendSenderLastName ?? null;

export const protectionInitiallyEnabled = Boolean(cfg.protectionInitiallyEnabled);

export const protectionInitiallyHasPassword = Boolean(cfg.protectionInitiallyHasPassword);

export const publicEditorMode = Boolean(cfg.publicEditorMode);

export const publicEditorIsAuthenticated = Boolean(cfg.publicEditorIsAuthenticated);

export const publicEditorSignatureUnlocked = Boolean(cfg.publicEditorSignatureUnlocked);

export const publicEditorSignatureUpgradeUrl = cfg.publicEditorSignatureUpgradeUrl ?? null;

export const publicEditorSignatureLoginUrl = cfg.publicEditorSignatureLoginUrl ?? null;

export const publicEditorSignatureRegisterUrl = cfg.publicEditorSignatureRegisterUrl ?? null;

export const publicEditorSignatureUrl = cfg.publicEditorSignatureUrl ?? null;

export const publicEditorDownloadUrl = cfg.publicEditorDownloadUrl ?? null;

export const publicEditorNeedsUpload = Boolean(cfg.publicEditorNeedsUpload);

export const publicEditorUploadMaxMb = Number(cfg.publicEditorUploadMaxMb ?? 25);

export const publicEditorMaxPages = Math.max(1, Number(cfg.publicEditorMaxPages ?? 200));

export const publicEditorUploadUrl = cfg.publicEditorUploadUrl ?? null;

export const documentLockedForDirectEdit = Boolean(cfg.documentLockedForDirectEdit);

export const pagesEl = document.getElementById('pdf-pages');

export const thumbsEl = document.getElementById('pdf-page-thumbs');

export const workspaceEl = document.querySelector('.pdf-editor-workspace');

export const canvasAreaEl = document.getElementById('pdf-canvas-area');

export const canvasHeaderEl = document.getElementById('pdf-canvas-header');

export const loadingEl = document.getElementById('pdf-editor-loading');

export const hintEl = document.getElementById('pdf-editor-hint');

export const alertEl = document.getElementById('pdf-editor-alert');

export const signatureSubbarEl = document.getElementById('pdf-signature-subbar');

export const signatureWorkspaceEl = document.getElementById('pdf-signature-workspace');

export const signatureIframeEl = document.getElementById('pdf-signature-iframe');

export const publicAuthModalEl = document.getElementById('pdf-public-auth-modal');

export const publicAuthCloseButton = document.getElementById('pdf-public-auth-close');

export const publicAuthTitleEl = document.getElementById('pdf-public-auth-title');

export const publicAuthSubtitleEl = document.getElementById('pdf-public-auth-subtitle');

export const publicAuthLoginLink = document.getElementById('pdf-public-auth-login');

export const publicAuthRegisterLink = document.getElementById('pdf-public-auth-register');

export const sendModalEl = document.getElementById('pdf-send-modal');

export const sendCloseButton = document.getElementById('pdf-send-close');

export const sendSubmitButton = document.getElementById('pdf-send-submit');

export const sendDocNameEl = document.getElementById('pdf-send-doc-name');

export const sendRecipientNameEl = document.getElementById('pdf-send-recipient-name');

export const sendRecipientEmailEl = document.getElementById('pdf-send-recipient-email');

export const sendSenderFirstEl = document.getElementById('pdf-send-sender-first');

export const sendSenderLastEl = document.getElementById('pdf-send-sender-last');

export const sendSenderEmailEl = document.getElementById('pdf-send-sender-email');

export const sendMessageEl = document.getElementById('pdf-send-message');

export const convertModalEl = document.getElementById('pdf-convert-modal');

export const convertCloseButton = document.getElementById('pdf-convert-close');

export const convertGridEl = document.getElementById('pdf-convert-grid');

export const convertFilenameEl = document.getElementById('pdf-convert-filename');

export const convertSubmitButton = document.getElementById('pdf-convert-submit');

export const compressModalEl = document.getElementById('pdf-compress-modal');

export const compressCloseButton = document.getElementById('pdf-compress-close');

export const compressSubmitButton = document.getElementById('pdf-compress-submit');

export const compressDownloadButton = document.getElementById('pdf-compress-download');

export const compressSourceNameEl = document.getElementById('pdf-compress-source-name');

export const compressSourceSizeEl = document.getElementById('pdf-compress-source-size');

export const compressOriginalSizeEl = document.getElementById('pdf-compress-original-size');

export const compressCompressedSizeEl = document.getElementById('pdf-compress-compressed-size');

export const compressMetaEl = document.getElementById('pdf-compress-meta');

export const mergeModalEl = document.getElementById('pdf-merge-modal');

export const mergeCloseButton = document.getElementById('pdf-merge-close');

export const mergeButton = document.getElementById('editor-merge');

export const mergeSubmitButton = document.getElementById('pdf-merge-submit');

export const mergeFileInputEl = document.getElementById('pdf-merge-file-input');

export const mergeDropEl = document.getElementById('pdf-merge-drop');

export const mergeSelectedEl = document.getElementById('pdf-merge-selected');

export const mergeSelectedNameEl = document.getElementById('pdf-merge-selected-name');

export const mergeSelectedSizeEl = document.getElementById('pdf-merge-selected-size');

export const mergeInsertAfterEl = document.getElementById('pdf-merge-insert-after');

export const mergeCurrentPagesEl = document.getElementById('pdf-merge-current-pages');

export const splitExtractModalEl = document.getElementById('pdf-split-extract-modal');

export const splitExtractCloseButton = document.getElementById('pdf-split-extract-close');

export const splitExtractSubmitButton = document.getElementById('pdf-split-extract-submit');

export const splitTabButtons = Array.from(document.querySelectorAll('[data-split-tab]'));

export const splitPanelSplitEl = document.getElementById('pdf-split-panel-split');

export const splitPanelExtractEl = document.getElementById('pdf-split-panel-extract');

export const splitRangesInputEl = document.getElementById('pdf-split-ranges');

export const extractRangesWrapEl = document.getElementById('pdf-extract-ranges-wrap');

export const extractRangesInputEl = document.getElementById('pdf-extract-ranges');

export const extractOptionInputs = Array.from(document.querySelectorAll('input[name="pdf-extract-option"]'));

export const splitSummaryEl = document.getElementById('pdf-split-summary');

export const splitPreviewGridEl = document.getElementById('pdf-split-preview-grid');

export const splitPreviewMetaEl = document.getElementById('pdf-split-preview-meta');

export const deletePagesModalEl = document.getElementById('pdf-delete-pages-modal');

export const deletePagesCloseButton = document.getElementById('pdf-delete-pages-close');

export const deletePagesSubmitButton = document.getElementById('pdf-delete-pages-submit');

export const deletePagesRangesInputEl = document.getElementById('pdf-delete-pages-ranges');

export const deletePagesSummaryEl = document.getElementById('pdf-delete-pages-summary');

export const deletePagesPreviewGridEl = document.getElementById('pdf-delete-pages-preview-grid');

export const deletePagesPreviewMetaEl = document.getElementById('pdf-delete-pages-preview-meta');

export const protectModalEl = document.getElementById('pdf-protect-modal');

export const protectCloseButton = document.getElementById('pdf-protect-close');

export const protectSubmitButton = document.getElementById('pdf-protect-submit');

export const protectEnabledEl = document.getElementById('pdf-protect-enabled');

export const protectEnabledStateEl = document.getElementById('pdf-protect-enabled-state');

export const protectFieldsEl = document.getElementById('pdf-protect-fields');

export const protectPasswordEl = document.getElementById('pdf-protect-password');

export const protectConfirmEl = document.getElementById('pdf-protect-confirm');

export const protectFeedbackEl = document.getElementById('pdf-protect-feedback');

export const protectRulesEl = document.getElementById('pdf-protect-rules');

export const protectTogglePasswordButton = document.getElementById('pdf-protect-toggle-password');

export const protectToggleConfirmButton = document.getElementById('pdf-protect-toggle-confirm');

export const watermarkModalEl = document.getElementById('pdf-watermark-modal');

export const watermarkCloseButton = document.getElementById('pdf-watermark-close');

export const watermarkApplyButton = document.getElementById('pdf-watermark-apply');

export const watermarkRemoveButton = document.getElementById('pdf-watermark-remove');

export const watermarkFeedbackEl = document.getElementById('pdf-watermark-feedback');

export const watermarkTabButtons = Array.from(document.querySelectorAll('[data-watermark-tab]'));

export const watermarkPanelTextEl = document.getElementById('pdf-watermark-panel-text');

export const watermarkPanelImageEl = document.getElementById('pdf-watermark-panel-image');

export const watermarkTextEl = document.getElementById('pdf-watermark-text');

export const watermarkFontEl = document.getElementById('pdf-watermark-font');

export const watermarkSizeEl = document.getElementById('pdf-watermark-size');

export const watermarkSizeValueEl = document.getElementById('pdf-watermark-size-value');

export const watermarkColorEl = document.getElementById('pdf-watermark-color');

export const watermarkOpacityEl = document.getElementById('pdf-watermark-opacity');

export const watermarkOpacityValueEl = document.getElementById('pdf-watermark-opacity-value');

export const watermarkRotationEl = document.getElementById('pdf-watermark-rotation');

export const watermarkRotationValueEl = document.getElementById('pdf-watermark-rotation-value');

export const watermarkImageInputEl = document.getElementById('pdf-watermark-image-input');

export const watermarkImagePickButton = document.getElementById('pdf-watermark-image-pick');

export const watermarkImageNameEl = document.getElementById('pdf-watermark-image-name');

export const watermarkImageScaleEl = document.getElementById('pdf-watermark-image-scale');

export const watermarkImageScaleValueEl = document.getElementById('pdf-watermark-image-scale-value');

export const statusEl = document.getElementById('pdf-action-status');

export const sizeInput = document.getElementById('pdf-tool-size');

export const sizeValueEl = document.getElementById('pdf-tool-size-value');

export const colorInput = document.getElementById('pdf-tool-color');

export const imageInput = document.getElementById('pdf-tool-image-input');

export const subbarEl = document.getElementById('pdf-editor-subbar');

export const textInputsEl = document.getElementById('pdf-text-inputs');

export const formFillBarEl = document.getElementById('pdf-form-fill-bar');

export const formFillSummaryEl = document.getElementById('pdf-form-fill-summary');

export const formFillPrevButton = document.getElementById('pdf-form-fill-prev');

export const formFillNextButton = document.getElementById('pdf-form-fill-next');

export const formFillFloatingNavEl = document.getElementById('pdf-form-fill-floating-nav');

export const formFillFloatingPrevButton = document.getElementById('pdf-form-fill-floating-prev');

export const formFillFloatingNextButton = document.getElementById('pdf-form-fill-floating-next');

export const formFillDetectButton = document.getElementById('pdf-form-fill-detect');

export const textFontEl = document.getElementById('pdf-text-font');

export const textBoldEl = document.getElementById('pdf-text-bold');

export const textItalicEl = document.getElementById('pdf-text-italic');

export const textUnderlineEl = document.getElementById('pdf-text-underline');

export const textAlignEl = document.getElementById('pdf-text-align');

export const editToolButtons = Array.from(document.querySelectorAll('[data-edit-tool]'));

export const suiteToolButtons = Array.from(document.querySelectorAll('[data-suite-tool]'));

export const undoButton = document.getElementById('editor-undo');

export const redoButton = document.getElementById('editor-redo');

export const printButton = document.getElementById('editor-print');

export const sendButton = document.getElementById('editor-send');

export const saveButton = document.getElementById('editor-save');

export const saveCopyButton = document.getElementById('editor-save-copy');

export const downloadButton = document.getElementById('editor-download');

export const documentRequiredTopActions = [printButton, sendButton, downloadButton, saveButton, saveCopyButton].filter(Boolean);

export const convertButton = document.getElementById('editor-convert');

export const compressButton = document.getElementById('editor-compress');

export const deletePagesActionButton = document.getElementById('editor-delete-pages');

export const splitExtractActionButton = document.getElementById('editor-split-extract');

export const protectActionButton = document.getElementById('editor-protect');

export const watermarkActionButton = document.getElementById('editor-watermark');

export const signatureOpenLinks = Array.from(document.querySelectorAll('[data-signature-open]'));

export const convertOptionButtons = Array.from(document.querySelectorAll('[data-convert-format]'));

export const publicEditorUploadButton = document.getElementById('public-editor-upload-button');

export const publicEditorUploadInput = document.getElementById('public-editor-upload-input');

export const publicEditorEmptyUploadButton = document.getElementById('public-editor-empty-upload-button');

export const uploadEmptyStateEl = document.getElementById('pdf-upload-empty-state');

export const docTitleEl = document.querySelector('.pdf-doc-title');

export const zoomPillEl = document.getElementById('pdf-zoom-pill');

export const zoomOutButton = document.getElementById('pdf-zoom-out');

export const zoomInButton = document.getElementById('pdf-zoom-in');

export const zoomLevelButton = document.getElementById('pdf-zoom-level');

export const pageInputEl = document.getElementById('pdf-page-input');

export const pageTotalEl = document.getElementById('pdf-page-total');

export const findBarEl = document.getElementById('pdf-find-bar');

export const findInputEl = document.getElementById('pdf-find-input');

export const findCountEl = document.getElementById('pdf-find-count');

export const findPrevButton = document.getElementById('pdf-find-prev');

export const findNextButton = document.getElementById('pdf-find-next');

export const findCloseButton = document.getElementById('pdf-find-close');

export const findOpenButton = document.getElementById('pdf-find-open');

export const draftBannerEl = document.getElementById('pdf-draft-banner');

export const draftBannerMetaEl = document.getElementById('pdf-draft-banner-meta');

export const draftRestoreButton = document.getElementById('pdf-draft-restore');

export const draftDiscardButton = document.getElementById('pdf-draft-discard');

export const replaceToolEnabled = true;

export const state = {
    renderGeneration: 0,
    basePdfBytes: null,
    pdfJsDoc: null,
    activeSuiteTool: 'edit',
    activeTool: 'pointer',
    annotations: [],
    pageLayouts: new Map(),
    pageTextFragments: new Map(),
    pageTextBlocks: new Map(),
    pageFormFields: new Map(),
    formFieldOverrides: new Map(),
    pageThumbPreviewUrls: new Map(),
    // Operation log entries: { kind: 'edit' | 'document', label, ... } —
    // see pushHistory/pushDocumentHistory in annotations.js.
    history: [],
    redo: [],
    // True while a document-level undo/redo is writing to the server.
    historyBusy: false,
    annotationDirty: false,
    interaction: null,
    draft: null,
    pendingImage: null,
    lastActivePage: 1,
    lastModeAlertAt: 0,
    // Document zoom: 1 = the width the pages were rendered at (fit width
    // at load). zoomMode 'fit' re-fits on resize; 'manual' keeps the level.
    zoom: 1,
    zoomMode: 'fit',
    baseRenderWidth: 0,
    zoomRerenderTimer: null,
    viewportWorkTimer: null,
    // Two-finger pinch over the document (see bindPinchZoom).
    // Depth counter for the drop-anywhere overlay (nested dragenter/leave).
    dropDepth: 0,
    // Which document already got the "N fields still blank" nudge at export.
    blanksNudgedDocKey: null,
    // rAF guard for repositioning the fixed chrome (find bar, props panel).
    chromeAnchorPending: false,
    pinch: {
        active: false,
        startDistance: 1,
        startZoom: 1,
        docX: 0,
        docY: 0,
    },
    // Progressive page pipeline bookkeeping (see preparePage).
    pagePrepare: new Map(),
    // Pages whose preparation threw for the current render generation; the
    // background queue skips them, a scroll into view may still retry.
    pagePrepareFailed: new Set(),
    pagesReady: new Set(),
    prepareQueueRunning: false,
    allPagesPreparedGeneration: -1,
    // Find in document (see runFind).
    find: {
        open: false,
        query: '',
        matches: [],
        index: -1,
        timer: null,
        token: 0,
    },
    colorTouched: false,
    // Set once the person picks a tool themselves; until then a document
    // with native form fields opens in Form Fill.
    userChoseTool: false,
    // Characters already reported as un-embeddable (fonts.js).
    unsupportedCharsWarned: null,
    suppressUnloadWarning: false,
    // Unsaved-work safety net: the annotation/form state is mirrored into
    // IndexedDB keyed by a hash of the loaded PDF bytes.
    localDraft: {
        key: null,
        timer: null,
        restorable: null,
        pendingRestore: null,
    },
    activeTextEditor: null,
    activeCommentEditor: null,
    activeReplaceEditor: null,
    activeBlockEditor: null,
    activeFormFieldEditor: null,
    selectedAnnotationId: null,
    convert: {
        format: 'pdf',
        busy: false,
    },
    send: {
        busy: false,
    },
    compress: {
        busy: false,
        originalBytes: 0,
        compressedBytes: 0,
        resultUrl: null,
        resultName: '',
    },
    merge: {
        busy: false,
        file: null,
        insertAfter: 0,
    },
    splitExtract: {
        busy: false,
        mode: 'split',
        extractOption: 'all',
    },
    deletePages: {
        busy: false,
    },
    protect: {
        busy: false,
        showPassword: false,
        showConfirm: false,
        enabled: !!protectionInitiallyEnabled,
        savedEnabled: !!protectionInitiallyEnabled,
        hasPassword: !!protectionInitiallyHasPassword,
        savedHasPassword: !!protectionInitiallyHasPassword,
        pendingPassword: '',
        modalSnapshotEnabled: !!protectionInitiallyEnabled,
    },
    watermark: {
        busy: false,
        enabled: false,
        pendingRemoval: false,
        appliedBaseBytes: null,
        type: 'text',
        text: 'CONFIDENTIAL',
        fontFamily: 'Helvetica',
        fontSize: 56,
        color: '#64748b',
        opacity: 0.2,
        rotation: -32,
        imageDataUrl: '',
        imageMimeType: '',
        imageAspectRatio: 1,
        imageScale: 0.45,
    },
    textStyle: {
        fontFamily: 'Helvetica',
        bold: false,
        italic: false,
        underline: false,
        align: 'left',
    },
};

export const toolHints = {
    pointer: 'Click around the PDF without adding anything. Click existing text, comments, images, or replacements to select.',
    explain: 'Click a paragraph for a plain-English reading of what it says. Descriptive only, not legal advice.',
    text: 'Click empty space to add new text. Click text you added to select and drag. Double-click selected added text to edit.',
    'form-fill': 'Detected fillable fields are highlighted. If a PDF has no native fields, click Detect Fields to scan underlines, drawn lines, and checkbox symbols.',
    replace: 'Click a paragraph to edit it like a document: type freely, press Enter for new lines, and delete lines or blank space to reflow the text. Click away or press Done to apply; Esc cancels.',
    shape: 'Click and drag to draw a rectangle.',
    highlight: 'Click and drag to create a highlight box.',
    underline: 'Click and drag horizontally to underline.',
    draw: 'Press and drag to draw freehand.',
    erase: 'Click and drag to cover an area with white. The covered content stays inside the file.',
    redact: 'Click and drag to place a black box. When you save, each redacted page is flattened into an image and the covered text is removed from the file for good.',
    image: 'Choose an image, then click to place. Click existing images to move or resize.',
    comment: 'Click to place a comment. Drag existing comments to reposition and double-click to edit.',
};

export const editToolLabels = {
    pointer: 'Pointer',
    explain: 'Explain',
    text: 'Add Text',
    replace: 'Edit Text',
    'form-fill': 'Form Fill',
    shape: 'Shape',
    highlight: 'Highlight',
    underline: 'Underline',
    draw: 'Draw',
    erase: 'Whiteout',
    redact: 'Redact',
    image: 'Image',
    comment: 'Comment',
};

export const suiteModeLabels = {
    edit: 'Edit',
    signature: 'Signature',
    convert: 'Convert',
    compress: 'Compress',
    merge: 'Merge',
    'delete-pages': 'Delete Pages',
    'split-extract': 'Split & Extract',
    protect: 'Protect',
    watermark: 'Watermark',
};

export const suiteModeMessages = {
    signature: 'Signature workflow is integrated. Use the top Signature bar or embedded setup to place signer fields and send requests.',
    convert: 'Choose an output format, then click Convert in the top-right to download.',
    compress: 'Click Compress in the top-right to reduce file size before downloading.',
    merge: 'Upload another PDF, choose the insertion page, then click Merge in the top-right.',
    'delete-pages': 'Choose pages to remove, preview them, then click Delete Pages in the top-right to remove and save.',
    'split-extract': 'Choose Split or Extract, define page ranges, then click Split & Extract in the top-right.',
    protect: 'Turn protection on or off, set a password, then click Save Edits. Future downloads will use that protection.',
    watermark: 'Choose text or image watermark options, click Apply, then Save Edits to stamp it once on every page. Saved watermarks cannot be undone.',
};

// ------------------------------------------------------------------
// Unsaved-work safety net (IndexedDB)
//
// The annotation list and form-field overrides are mirrored into
// IndexedDB shortly after every change, keyed by a hash of the loaded PDF
// bytes. When the same document loads again with no edits in memory, the
// banner offers to bring the draft back. A successful save clears it.
// ------------------------------------------------------------------
export const DRAFT_DB_NAME = 'legaltemplates-pdf-editor';

export const DRAFT_STORE_NAME = 'drafts';

export const DRAFT_VERSION = 1;

export const draftStore = {
    get: (key) => draftDbRequest('readonly', (store) => store.get(key)),
    put: (key, value) => draftDbRequest('readwrite', (store) => store.put(value, key)),
    delete: (key) => draftDbRequest('readwrite', (store) => store.delete(key)),
};

// ------------------------------------------------------------------
// Block text editing ("Edit Text" 2.0)
//
// Groups the page's extracted text fragments into lines and lines into
// editable blocks (a column of text is one block). Clicking text opens a
// contenteditable region over the whole block so users get real document
// editing: type freely, delete lines, remove blank space, join/split
// lines — the block reflows. Committing produces an `edit-block`
// annotation: a background mask over the block's original area plus the
// reflowed lines, rendered identically in the SVG preview and the
// exported PDF.
// ------------------------------------------------------------------
export const BLOCK_DEFAULT_ASCENT = 0.86;

export const BLOCK_DEFAULT_DESCENT = 0.14;

// ------------------------------------------------------------------
// Document zoom
//
// Pages are rendered once at a base width (fit-to-width at load) and every
// annotation, text fragment and form field keeps its coordinates in that
// base space. Zooming applies CSS `zoom` to the page stack, so the only
// places that need to know about it are the pointer→page conversions
// below. Once a zoom level settles, the canvases near the viewport are
// re-rendered at the matching resolution so text stays crisp.
// ------------------------------------------------------------------
export const ZOOM_STEPS = [0.5, 0.67, 0.8, 0.9, 1, 1.1, 1.25, 1.5, 1.75, 2, 2.5, 3, 4];

export const ZOOM_MAX = 4;

export const ZOOM_MAX_BACKING_WIDTH = 2600;

export const DRAG_TOOLS = new Set(['draw', 'shape', 'highlight', 'underline', 'erase', 'redact']);

export const toolDefaultColors = {
    highlight: '#fde047',
    pointer: '#111111',
    text: '#111111',
    replace: '#111111',
    shape: '#111111',
    underline: '#111111',
    draw: '#111111',
    comment: '#111111',
    image: '#111111',
    'form-fill': '#111111',
};

// ------------------------------------------------------------------
// Shell chrome (Phase 1): menus, tool groups, phone sheets
//
// The toolbar shows five verbs; each sub-tool keeps its data-edit-tool
// hook so activateTool() is untouched. Pages/Export menu items reuse the
// legacy suite-mode buttons and the mode-specific top-bar buttons, both of
// which stay in the DOM out of sight.
// ------------------------------------------------------------------
export const shellMenuTriggers = Array.from(document.querySelectorAll('[data-menu-trigger]'));

export const shellMenuPanels = Array.from(document.querySelectorAll('[data-menu-panel]'));

export const shellMenuBackdrop = document.getElementById('pdf-menu-backdrop');

export const workspaceShellEl = document.getElementById('pdf-editor-workspace');

export const THUMBS_PREF_KEY = 'pdfEditorThumbnails';

export const editSuiteModes = new Set(['edit', 'signature']);

// ------------------------------------------------------------------
// Progressive page pipeline
//
// Loading a document no longer renders every page before the editor is
// usable. Pass 1 sizes every page shell and thumbnail placeholder (cheap:
// page dictionaries only). Pages near the viewport are then "prepared"
// (canvas render + text fragments + form fields + thumbnail) first, and a
// background loop prepares the rest outward from the current page. Far
// pages of long documents give their canvas memory back and come back
// when scrolled near again.
// ------------------------------------------------------------------
export const THUMB_WIDTH = 160;

export const VIRTUALIZE_MIN_PAGES = 12;
